/**
 * HAKI Universal Aluminium S4 component data.
 *
 * The single place a part number or weight appears. Art. nos. and weights come
 * from the RISE product certificate C900677 and the HAKI 2024 component list;
 * geometry constants come from the drawings named in `docs/SOURCES.md`.
 * `data/haki-config.json` carries the same data for other tools — this module
 * is inlined so the app needs no fetch.
 */

export type ArtTable = Readonly<Record<number, string>>;
export type WeightTable = Readonly<Record<number, number>>;

/** A component family available in several lengths. */
export type PartTable = Readonly<{
  art: ArtTable;
  kg: WeightTable;
}>;

export type PlankWidth = 170 | 230;

/** Millimetres to metres — the model works in metres, the data in millimetres. */
export const MM = 0.001;

/* -----------------------------------------------------------------------------
   Drawing constants — T-4017306 (Spira S4 3000 AL)
   ----------------------------------------------------------------------------- */

/** Lower cup collar, metres above the node. */
export const KL = 0.2;

/** Upper cup collar, metres above the node (200 + 130 mm). */
export const KR = 0.33;

/** Spigot protruding above the tube, metres. */
export const TAPP = 0.16;

/** Cup-group pitch, metres. */
export const NODE = 0.5;

/**
 * Default absolute height of node level 0 — the minimum base-jack extension.
 * The live value is the base datum below.
 */
export const BASE_DATUM_DEFAULT_MM = 55;

/**
 * How far the base datum may be wound out, millimetres.
 *
 * The grid is periodic with the 500 mm node pitch, so a datum of 555 mm is the
 * same geometry as 55 mm one level up. The ceiling stops just short of that
 * wrap; the floor is the BS jack's minimum extension.
 */
export const BASE_DATUM_MIN_MM = 55;
export const BASE_DATUM_MAX_MM = 550;

/** Deck planks sit this far above the node: the upper cup collar plus the hook. */
export const DECK_LIFT = KR + 0.016;

/** ALP profile thickness, metres. */
export const PLANK_THICKNESS = 0.09;

/** Guardrail rail heights above the deck floor. RISE C900677 requires ≥ 950 mm. */
export const GUARDRAIL_MID_HEIGHT = 0.5;
export const GUARDRAIL_TOP_HEIGHT = 1.0;

/**
 * How far above its floor node a guardrail's top connector sits.
 *
 * A standard carrying a guardrail must reach at least this high, or the frame's
 * upper hook has nothing to latch onto — see `ensureGuardrailPosts`.
 */
export const GUARDRAIL_TOP_ABOVE_NODE = DECK_LIFT + PLANK_THICKNESS + GUARDRAIL_TOP_HEIGHT;

/** Beam module series, millimetres. */
export const MODS: readonly number[] = [3050, 2500, 1964, 1655, 1250, 1050, 770, 700];

/**
 * Height of a bracket's outer tube — the stub carrying its cup group and Ø38
 * spigot, metres. It spans exactly one node pitch, so a standard set on the
 * spigot starts at the next node level and its cups stay on the global grid.
 */
export const BRACKET_STUB = NODE;

/**
 * How many node levels below the bracket the SKD 1250 diagonal hooks into the
 * standard. The manuals give no figure; 1.5 m (a 45° strut over the 1250 mm
 * reach) is read off the product photo and the steel/aluminium weight gap —
 * see `docs/SOURCES.md`.
 */
export const SKD_HOOK_LEVELS_BELOW = 3;

/** Reach of the SKD 1250 bracket diagonal, millimetres — the beam it combines with. */
export const SKD_LENGTH = 1250;

/** Standard segment lengths, millimetres, longest first. */
export const SPIRE_LENGTHS: readonly number[] = [3000, 2000, 1500, 1000, 500];

/** Single fixed component — one art. no., one weight. */
export type SinglePart = Readonly<{ art: string; kg: number }>;

export type HakiConfig = Readonly<{
  jackMin: number;
  jackMax: number;
  spire: Readonly<{ lengths: readonly number[]; art: ArtTable; kg: WeightTable }>;
  jack: SinglePart;
  lb: PartTable;
  erb: PartTable;
  skrd: PartTable;
  ds: PartTable;
  alp: Readonly<Record<PlankWidth, PartTable>>;
  hatch: Readonly<{ width: number; art: ArtTable; kg: WeightTable }>;
  toe: PartTable;
  ladder: Readonly<{ name: string; art: string; kg: number; len: number; width: number; rung: number }>;
  utv: Readonly<{ width: number; art: ArtTable; kg: WeightTable }>;
  hl: PartTable;
  hli: SinglePart;
  hliGalvanised: SinglePart;
  plankRider: Readonly<{ long: PartTable; short: PartTable }>;
  safetyHoop: SinglePart;
  spireHolder: Readonly<Record<number, SinglePart | undefined>>;
  fzv: Readonly<Record<number, SinglePart | undefined>>;
  /** Brackets with spigot (SK) keyed by reach; the SKD 1250 diagonal keyed by the beam it braces. */
  bracket: Readonly<{ sk: PartTable; skd: PartTable }>;
}>;

export const CFG: HakiConfig = {
  jackMin: 55,
  jackMax: 570,
  spire: {
    lengths: SPIRE_LENGTHS,
    art: { 3000: "4017306", 2000: "4017206", 1500: "4017156", 1000: "4017106", 500: "4017056" },
    kg: { 3000: 7.7, 2000: 5.2, 1500: 4.0, 1000: 2.8, 500: 1.6 },
  },
  jack: { art: "2071000", kg: 5.0 },
  lb: {
    art: {
      350: "4021031",
      700: "4021066",
      770: "4021073",
      1050: "4021101",
      1250: "4021121",
      1655: "4021161",
      1964: "4021191",
      2500: "4021246",
      3050: "4021301",
    },
    kg: { 350: 1.9, 700: 2.7, 770: 2.9, 1050: 3.5, 1250: 4.2, 1655: 4.8, 1964: 5.8, 2500: 7.2, 3050: 8.5 },
  },
  erb: {
    art: {
      564: "4022051",
      700: "4022066",
      770: "4022073",
      1050: "4022101",
      1250: "4022121",
      1655: "4022161",
      1964: "4022191",
      2500: "4022246",
      3050: "4022301",
    },
    kg: { 564: 2.5, 700: 2.7, 770: 2.9, 1050: 3.2, 1250: 3.6, 1655: 4.1, 1964: 4.5, 2500: 5.4, 3050: 6.2 },
  },
  skrd: {
    art: {
      700: "4052066",
      770: "4052073",
      1050: "4052101",
      1250: "4052121",
      1655: "4052161",
      1964: "4052191",
      2500: "4052246",
      3050: "4052301",
    },
    kg: { 700: 3.3, 770: 3.5, 1050: 4.5, 1250: 4.8, 1655: 5.9, 1964: 6.6, 2500: 7.8, 3050: 8.9 },
  },
  ds: {
    art: { 1250: "4122120", 1655: "4122160", 1964: "4122190", 2500: "4122245", 3050: "4122300" },
    kg: { 1250: 4.5, 1655: 4.9, 1964: 5.4, 2500: 6.1, 3050: 6.7 },
  },
  alp: {
    170: {
      art: {
        770: "2153081",
        1050: "2153106",
        1250: "2153126",
        1655: "2153166",
        1964: "2153196",
        2500: "2153247",
        3050: "2153306",
      },
      kg: { 770: 2.6, 1050: 3.4, 1250: 4.0, 1655: 5.2, 1964: 6.1, 2500: 7.6, 3050: 9.2 },
    },
    230: {
      art: {
        770: "2153080",
        1050: "2153100",
        1250: "2153120",
        1655: "2153160",
        1964: "2153190",
        2500: "2153245",
        3050: "2153300",
      },
      kg: { 770: 4.1, 1050: 4.9, 1250: 5.6, 1655: 6.9, 1964: 7.8, 2500: 9.5, 3050: 11.2 },
    },
  },
  hatch: {
    width: 690,
    art: { 1250: "4098123", 1655: "4098163", 1964: "4098193", 2500: "4098253", 3050: "4098303" },
    kg: { 1250: 19.9, 1655: 23.7, 1964: 26.6, 2500: 31.6, 3050: 36.7 },
  },
  /** Weights from drawing T-4161121. */
  toe: {
    art: {
      300: "4161031",
      564: "4161051",
      700: "4161071",
      770: "4161077",
      1050: "4161105",
      1250: "4161121",
      1550: "4161151",
      1655: "4161161",
      1964: "4161191",
      2050: "4161201",
      2210: "4161221",
      2357: "4161231",
      2500: "4161251",
      2550: "4161255",
      3050: "4161301",
    },
    kg: {
      300: 0.65,
      564: 1.03,
      700: 1.27,
      770: 1.4,
      1050: 1.91,
      1250: 2.18,
      1550: 2.82,
      1655: 2.91,
      1964: 3.46,
      2050: 3.64,
      2210: 3.98,
      2357: 4.24,
      2500: 4.55,
      2550: 4.64,
      3050: 5.47,
    },
  },
  /** T-2091210. */
  ladder: { name: "Ladder ST 2100 AL", art: "2091210", kg: 3.4, len: 2145, width: 383, rung: 300 },
  /** Price list: UTV AL with rest platform and locking, 44°, 200 mm per step. */
  utv: {
    width: 600,
    art: { 500: "4102055", 1000: "4102105", 1500: "4102155", 2500: "4102247", 3050: "4102302" },
    kg: { 500: 7.5, 1000: 11.2, 1500: 16.2, 2500: 22.9, 3050: 29.2 },
  },
  /** T-4058300 — outer handrail for UTV ×2000. */
  hl: { art: { 3050: "4058300", 2500: "4058245" }, kg: { 3050: 10.3, 2500: 9.2 } },
  /** Inner handrail HLI UTV AL. */
  hli: { art: "7058253", kg: 11.4 },
  /** Galvanised inner handrail — the same part in steel. Catalogue HLI UTV. */
  hliGalvanised: { art: "7058256", kg: 10.4 },
  /**
   * Plankerytter — plank rider, straddling 1, 2 or 3 boards. Keyed by the width
   * it spans in millimetres. Two profiles: a long arm with an offset end, and a
   * short symmetric one. Galvanised.
   */
  plankRider: {
    long: {
      art: { 230: "7218034", 460: "7218023", 700: "7218035" },
      kg: { 230: 3.0, 460: 4.0, 700: 5.2 },
    },
    short: {
      art: { 230: "7218032", 460: "7218033" },
      kg: { 230: 3.0, 460: 3.5 },
    },
  },
  /** Sikkerhetsbøyle — safety hoop, clamps onto a ledger beam. Galvanised. */
  safetyHoop: { art: "7053110", kg: 5.5 },
  /**
   * Spireholder — clamps a tube and presents a spigot, so a standard can be
   * started where there is no cup group. Keyed by the tube diameter it grips:
   * Ø50 locks with a 12 mm pin, Ø34 with a screw.
   */
  spireHolder: {
    50: { art: "7208025", kg: 2.3 },
    34: { art: "7208018", kg: 3.9 },
  },
  /** UTV AL Rekkverk 1000/1500 Fzv — info sheet; mounts on the stringer. */
  fzv: {
    1000: { art: "7058112", kg: 10.5 },
    1500: { art: "7058155", kg: 13.0 },
  },
  /**
   * Konsoll — HAKI Universal S4 AL manual, "Other components". Both hook a
   * cup group with a spring locking catch and end in a cup group + Ø38 spigot.
   * The SK carries its own arm; the SKD is a diagonal combined with an ERB or
   * LB 1250 as the arm.
   */
  bracket: {
    sk: { art: { 770: "4211074", 564: "4211052" }, kg: { 770: 3.9, 564: 3.6 } },
    skd: { art: { 1250: "4212002" }, kg: { 1250: 7.8 } },
  },
};

/** Bracket-with-spigot reaches the table carries, longest first. */
export const BRACKET_SK_LENGTHS: readonly number[] = Object.keys(CFG.bracket.sk.art)
  .map(Number)
  .sort((a, b) => b - a);

/**
 * SK: Konsoll SK 564/770 AL with spigot — its own arm hooks the cup group.
 * SKD: Konsolldiagonal SKD 1250 AL — a diagonal from a cup group below,
 * combined with an ERB/LB 1250 as the arm.
 */
export type BracketKind = keyof HakiConfig["bracket"];

/** Catalogue name of a bracket, as the component list and the HUD show it. */
export function bracketName(kind: BracketKind, len: number): string {
  return kind === "sk" ? `Bracket with spigot SK ${len} AL` : `Bracket diagonal SKD ${len} AL`;
}

/** Plank lengths the ALP tables carry, ascending. */
export const ALP_LENGTHS: readonly number[] = Object.keys(CFG.alp[230].art)
  .map(Number)
  .sort((a, b) => a - b);

/** Toe-board lengths the table carries, ascending. */
export const TOE_LENGTHS: readonly number[] = Object.keys(CFG.toe.art)
  .map(Number)
  .sort((a, b) => a - b);

/**
 * The base datum, metres: the absolute height of node level 0, and therefore
 * the base-jack extension every standard on level ground ends up with.
 *
 * It is module state rather than a parameter because it is the app's single
 * absolute reference — `nodeY` is called from roughly every render pass and
 * tool, and threading a datum through all of them would obscure far more than
 * it clarifies. Change it only through `setBaseDatumMm`, and re-render after.
 */
let baseDatum = BASE_DATUM_DEFAULT_MM * MM;

export function getBaseDatumMm(): number {
  return Math.round(baseDatum / MM);
}

/** Set the datum, clamped to the jack's usable range. Returns the value applied. */
export function setBaseDatumMm(mm: number): number {
  const clamped = Math.min(BASE_DATUM_MAX_MM, Math.max(BASE_DATUM_MIN_MM, Math.round(mm)));
  baseDatum = clamped * MM;

  return clamped;
}

/** Absolute height of node level `p`. Every part references a level, never a metre value. */
export function nodeY(p: number): number {
  return baseDatum + p * NODE;
}

/** First node level a base jack under ground height `gy` can reach. */
export function basePFor(gy: number): number {
  return Math.ceil((gy + CFG.jackMin * MM - baseDatum) / NODE - 1e-6);
}
